import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Historical import confirm can process 1000+ rows and easily runs 60-120s;
// give it a wide 5-minute ceiling so the ingress doesn't guillotine it early.
const LONG_RUNNING_PATH_PATTERNS = [
  /^\/?api\/imports\//i,
];

function isLongRunning(url) {
  if (!url) return false;
  return LONG_RUNNING_PATH_PATTERNS.some((re) => re.test(url));
}

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  timeout: 60000,  // 60s default is generous for normal endpoints
});

// Widen the timeout for long-running import endpoints and attach the auth token.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isLongRunning(config.url)) {
    config.timeout = 5 * 60 * 1000;  // 5 minutes
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error.response?.status === 401) {
      // Attempt refresh once
      try {
        await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api.request(error.config);
      } catch {
        localStorage.removeItem("access_token");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return String(e);
        const field = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(" › ") : "";
        const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
        return field ? `${field}: ${msg}` : msg;
      })
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Extract a human-friendly message from an axios error. */
export function apiErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  const detail = err.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  return err.response?.data?.message || err.message || fallback;
}

export default api;
